import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../store/store';
import IconButton, { ICON_BUTTON_TYPES } from '../../../components/buttons/IconButton';
import { FEEDBACK_PROFILER_FIELDS, getErrorFields } from '../../../utils/constants/fields';
import TextArea from '../../../components/inputs/TextArea';
import MultiSelect from '../../../components/inputs/MultiSelect';
import { mapFieldsToState } from '../../../utils/utils';
import { buildColleagueOptions } from '../../../utils/colleagueOptions';
import { useEffect } from 'react';

const FeedbackPopup = observer(({ toggle = () => {}, projectId = '' }) => {
  const { profilerProjectStore, profilerAccessStore } = useStore();
  const state = useLocalObservable(() => ({
    rendered: true,
    unrender: () => (state.rendered = false),
    fields: mapFieldsToState(FEEDBACK_PROFILER_FIELDS),
    setFieldValue: (field = {}, value) => {
      state.fields[field.NAME] = value;
    },
    get validationFields() {
      return getErrorFields(Object.values(FEEDBACK_PROFILER_FIELDS), state.fields);
    },
    get usersList() {
      return buildColleagueOptions(profilerAccessStore.allowedRequestFeedbackUsersList);
    },
    overlayHeight: window.visualViewport.height + 2000,
    updateOverlayHeight: () => (state.overlayHeight = window.visualViewport.height + 2000)
  }));

  useEffect(() => {
    const onViewportResize = () => {
      state.updateOverlayHeight();
    };
    window.visualViewport.addEventListener('resize', onViewportResize);
    return () => {
      state.unrender();
      window.visualViewport.removeEventListener('resize', onViewportResize);
    };
  }, [state]);

  const requestFeedback = () => {
    profilerProjectStore.requestProjectFeedback(
      projectId,
      state.fields[FEEDBACK_PROFILER_FIELDS.COLLEAGUES.NAME],
      state.fields[FEEDBACK_PROFILER_FIELDS.NOTE.NAME],
      () => {
        if (state.rendered) {
          toggle();
        }
      }
    );
  };

  const isSendButtonDisabled =
    state.fields[FEEDBACK_PROFILER_FIELDS.COLLEAGUES.NAME].length === 0 ||
    state.fields[FEEDBACK_PROFILER_FIELDS.NOTE.NAME] === '' ||
    profilerProjectStore.isRequestingProjectFeedback;

  return (
    <div className="overlay" style={{ height: state.overlayHeight + 'px' }}>
      <div className="container popup-feedback">
        <div className="header feedback">
          <div className="text">Request feedback from your team</div>
          <div className="close" onClick={toggle}>
            Close
          </div>
        </div>
        <div className="separator"></div>
        <div className="content">
          <MultiSelect
            field={FEEDBACK_PROFILER_FIELDS.COLLEAGUES}
            value={state.fields[FEEDBACK_PROFILER_FIELDS.COLLEAGUES.NAME]}
            values={state.usersList}
            setFieldValue={state.setFieldValue}
            disabled={!!profilerProjectStore.isRequestingProjectFeedback}
            inputWrap={{ enable: true, className: 'field' }}
          />
          <div className="info colleagues">
            Only colleagues with active profiler access are shown.
          </div>
          <TextArea
            field={FEEDBACK_PROFILER_FIELDS.NOTE}
            value={state.fields[FEEDBACK_PROFILER_FIELDS.NOTE.NAME]}
            setFieldValue={state.setFieldValue}
            disabled={!!profilerProjectStore.isRequestingProjectFeedback}
            messages={state.validationFields.messages}
            showOnSubmitErrorState={true}
            inputWrap={{ enable: true, className: 'field' }}
          />
          <div className="info">
            Feedback requests are sent individually. Only the requestor can view consolidated
            feedbacks.
          </div>
          <div className="actions">
            <IconButton
              type={ICON_BUTTON_TYPES.BLUE}
              innerText="Send"
              disabled={isSendButtonDisabled}
              onClick={requestFeedback}
              filled
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default FeedbackPopup;
